import { ref } from "vue";
import { httpApi } from "@/utils/http";

export type NotificationDisplayPage = "risk_monitor" | "order_blotter";
export interface NotificationDisplayPolicies {
  defaults: Record<string, NotificationDisplayPage[]>;
  policies: Array<{ category: string; display_pages: NotificationDisplayPage[] }>;
}

const policies = ref<Record<string, NotificationDisplayPage[]>>({});
const page = ref<NotificationDisplayPage | null>(null);
const loading = ref(false);
const loaded = ref(false);
const error = ref("");
let timer: ReturnType<typeof setInterval> | null = null;
let epoch = 0;
let pending: Promise<void> | null = null;

function admits(category: string): boolean {
  return (
    loaded.value &&
    page.value !== null &&
    Object.prototype.hasOwnProperty.call(policies.value, category) &&
    policies.value[category].includes(page.value)
  );
}

function refresh(): Promise<void> {
  if (pending) return pending;
  const expectedEpoch = epoch;
  loading.value = true;
  const request = (async () => {
    try {
      const response = await httpApi.get<NotificationDisplayPolicies>(
        "/notification-display-policies/",
      );
      if (expectedEpoch !== epoch) return;
      if (
        !response ||
        !response.defaults ||
        !Array.isArray(response.policies) ||
        response.policies.some(
          (policy) =>
            typeof policy.category !== "string" ||
            !Array.isArray(policy.display_pages) ||
            policy.display_pages.some(
              (value) => value !== "risk_monitor" && value !== "order_blotter",
            ),
        )
      ) {
        throw new Error("展示策略响应格式错误");
      }
      policies.value = Object.fromEntries(
        response.policies.map((policy) => [policy.category, policy.display_pages]),
      );
      loaded.value = true;
      error.value = "";
    } catch {
      if (expectedEpoch !== epoch) return;
      error.value = loaded.value
        ? "通知展示策略刷新失败，正在使用上次成功配置"
        : "通知展示策略加载失败，通知暂不展示";
    } finally {
      if (expectedEpoch === epoch) {
        loading.value = false;
        pending = null;
      }
    }
  })();
  pending = request;
  return request;
}

function start(nextPage: NotificationDisplayPage): void {
  page.value = nextPage;
  void refresh();
  if (timer === null) timer = setInterval(() => void refresh(), 60_000);
}

function stop(reset = false): void {
  page.value = null;
  if (timer !== null) clearInterval(timer);
  timer = null;
  epoch += 1;
  pending = null;
  loading.value = false;
  if (reset) {
    policies.value = {};
    loaded.value = false;
    error.value = "";
  }
}

export function useNotificationDisplayPolicy() {
  return { admits, start, stop, refresh, loading, loaded, error };
}
